import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { useCart } from "@/lib/cart";
import { api, imgUrl, eur, errMsg } from "@/lib/api";
import PayseraPay from "@/components/shop/PayseraPay";
import { ContactFields, EMPTY_CUSTOMER, contactValid, consentMissing } from "@/components/shop/ContactFields";
import { ExVat, Totals, calcTotals } from "@/components/shop/Totals";
import { Loader2, Truck, CreditCard } from "lucide-react";

const METHODS = [["paysera", CreditCard], ["cod", Truck]];

const MethodPicker = ({ method, setMethod }) => {
  const { t } = useLang();
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="payment-method-picker">
      {METHODS.map(([m, Icon]) => (
        <button type="button" key={m} data-testid={`payment-method-${m}`} onClick={() => setMethod(m)}
          className={`text-left p-4 border transition-colors ${method === m ? "border-orange-500 bg-orange-500/10" : "border-[#3B4157] hover:border-slate-500"}`}>
          <div className="flex items-center gap-2 font-display font-bold uppercase tracking-wider text-sm"><Icon className="w-4 h-4 text-orange-500" />{t(`pm_${m}`)}</div>
          <div className="text-xs text-slate-400 mt-1">{t(`pm_${m}_sub`)}</div>
        </button>
      ))}
    </div>
  );
};

const CodSubmit = ({ buildOrder, onPaid }) => {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { onPaid(await buildOrder()); } catch { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <p data-testid="cod-note" className="text-sm text-amber-400 border-l-2 border-amber-400 pl-3">{t("cod_note")}</p>
      <button data-testid="cod-submit-btn" onClick={submit} disabled={busy}
        className="w-full h-12 bg-orange-500 text-[#1F2230] font-display font-bold uppercase tracking-widest hover:bg-orange-400 disabled:opacity-60 inline-flex items-center justify-center gap-2 transition-colors">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />} {t("cod_submit")}
      </button>
    </div>
  );
};

const Summary = ({ items, total, cod, shipping }) => {
  const { t } = useLang();
  const tt = calcTotals(total, cod, shipping);
  return (
    <aside className="border border-[#3B4157] bg-[#292D3C] p-6 h-fit lg:sticky lg:top-24" data-testid="checkout-summary">
      <h2 className="font-display font-bold uppercase text-xl">{t("summary")}</h2>
      <div className="space-y-3 mt-5">
        {items.map((i) => (
          <div key={i.id} className="flex gap-3 text-sm">
            <img src={imgUrl(i.image)} alt="" className="w-12 h-12 object-contain p-0.5 img-stage" />
            <div className="flex-1 min-w-0"><div className="truncate">{i.name}</div><div className="text-slate-500 font-mono-x text-xs">{i.quantity} × {eur(i.price)}<ExVat /></div></div>
            <div className="font-mono-x whitespace-nowrap">{eur(i.price * i.quantity)}<ExVat /></div>
          </div>
        ))}
      </div>
      <div className="border-t border-[#3B4157] mt-5 pt-4 space-y-2 text-sm">
        <Totals net={tt.net} vat={tt.vat} shipping={tt.shipping} total={tt.total} testid="checkout-total" />
      </div>
    </aside>
  );
};

export default function Checkout() {
  const { t } = useLang();
  const { items, total, clear, shipping } = useCart();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [c, setC] = useState(EMPTY_CUSTOMER);
  const [method, setMethod] = useState("paysera");

  if (!items.length)
    return (
      <div className="text-center py-32" data-testid="checkout-empty">
        <p className="text-slate-400">{t("cart_empty")}</p>
        <Link to="/catalog" className="inline-block mt-6 text-orange-500 uppercase tracking-widest font-display font-semibold">{t("continue_shopping")} →</Link>
      </div>
    );

  const next = (e) => {
    e.preventDefault();
    if (!contactValid(c)) return toast.error(t("fill_all"));
    if (consentMissing(c, t, toast)) return;
    setStep(2);
  };

  const buildOrder = async () => {
    try {
      const { data } = await api.post("/orders", { items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })), customer: c, payment_method: method });
      return data;
    } catch (e) {
      toast.error(errMsg(e));
      throw e;
    }
  };

  const onPaid = (order) => {
    clear();
    nav(`/order/${order.id}`);
  };


  return (
    <div data-testid="checkout-page" className="mx-auto max-w-7xl px-4 sm:px-6 py-12">
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight">{t("checkout_title")}</h1>
      <div className="grid lg:grid-cols-[1fr_380px] gap-10 mt-10">
        <div className="space-y-6">
          <section className={`border p-6 ${step === 1 ? "border-orange-500/60 bg-[#30354A]" : "border-[#3B4157]"}`}>
            <div className="flex justify-between items-center">
              <h2 className="font-display font-bold uppercase text-xl"><span className="text-orange-500 font-mono-x mr-2">01</span>{t("contacts")}</h2>
              {step === 2 && <button data-testid="checkout-edit-btn" onClick={() => setStep(1)} className="text-sm text-orange-500">{t("edit")}</button>}
            </div>
            {step === 1 ? (
              <form onSubmit={next} className="grid sm:grid-cols-2 gap-4 mt-6">
                <ContactFields c={c} setC={setC} />
                <button data-testid="checkout-continue-btn" type="submit" className="sm:col-span-2 h-12 bg-orange-500 text-[#1F2230] font-display font-bold uppercase tracking-widest hover:bg-orange-400 transition-colors">
                  {t("to_payment")}
                </button>
              </form>
            ) : (
              <p className="text-sm text-slate-400 mt-3">
                {c.customer_type === "company" && <>{c.company} · {t("company_code")}: {c.company_code}{c.vat_code && ` · PVM: ${c.vat_code}`}<br /></>}
                {c.name} · {c.email} · {c.phone}<br />{c.address}, {c.city} {c.postal_code}, {c.country}</p>
            )}
          </section>
          <section className={`border p-6 ${step === 2 ? "border-orange-500/60 bg-[#30354A]" : "border-[#3B4157] opacity-50"}`}>
            <h2 className="font-display font-bold uppercase text-xl"><span className="text-orange-500 font-mono-x mr-2">02</span>{t("payment")}</h2>
            {step === 2 && (
              <div className="mt-6 space-y-6">
                <MethodPicker method={method} setMethod={setMethod} />
                <div className="max-w-md">{method === "cod" ? <CodSubmit buildOrder={buildOrder} onPaid={onPaid} /> : <PayseraPay buildOrder={buildOrder} onPaid={onPaid} />}</div>
              </div>
            )}
          </section>
        </div>
        <Summary items={items} total={total} shipping={shipping} cod={step === 2 && method === "cod"} />
      </div>
    </div>
  );
}
